import {
  INITIAL_CONFIG as BASE_INITIAL_CONFIG,
  type RemoteConfig as BaseRemoteConfig,
  SummarySection as BaseSummarySection,
} from '@web-solutions/base-app/constants/remote-config';
import type { BaseOfferProductTypes } from '@web-solutions/core/constants/remote-config';
import { type Nullable } from '@web-solutions/core/constants/remote-config';
import { InterButtonConfig } from '@web-solutions/core/payment/constants';
import { CoreSummarySection } from '@web-solutions/core/summary/types';

import { type QuizRoute } from './routes';
import type { AfterPurchaseRoutes } from './screens';

enum LocalSummarySection {
  FIND_OUT = 'find_out',
  PLAN = 'plan',
}

export const SummarySection = { ...LocalSummarySection, ...BaseSummarySection };
export type SummarySectionType = LocalSummarySection | BaseSummarySection;

export enum OfferProductId {}

export type OfferProductTypes = BaseOfferProductTypes<OfferProductId>;

export type AfterPurchaseFlow = AfterPurchaseRoutes;

export interface RemoteConfig
  extends Omit<
    BaseRemoteConfig<QuizRoute>,
    'landingStack' | 'landingButtons' | 'emailPageSections' | 'magicType'
  > {
  landingStack: Array<SummarySectionType | CoreSummarySection>;
  landingButtons: Array<InterButtonConfig<SummarySectionType>>;
  emailPageSections: Array<SummarySectionType>;
  afterPurchaseFlow: AfterPurchaseFlow;
  ebookScreenData: any;
  ebookProducts: any;
  mailTemplates: any;
  oneProductBlock: boolean;
  upsaleRoutes: string[];
  unavailableProduct: string[];
}

export const INITIAL_CONFIG: RemoteConfig = {
  ...BASE_INITIAL_CONFIG,
  // Тема воронки. В Magnus параметр `theme` задаётся json-ом:
  // {"mode": "dark"} — чёрная тема, {"mode": "light"} (или пусто) — белая.
  // Отдельные токены можно переопределить точечно:
  // {"mode": "dark", "colors": {"themeSurface": "#111111"}}
  theme: { mode: 'light' },
  flow: [],
  consentCookieModal: {
    enabled: false,
    type: 'long',
    placement: 'WELCOME',
  },
  afterPurchaseFlow: [],
  ebookScreenData: [],
  ebookProducts: {},
  mailTemplates: {},
  oneProductBlock: false,
  upsaleRoutes: [],
  unavailableProduct: [],
};

export type FullConfig = Nullable<Partial<RemoteConfig>>;

/**
 * Пути, где пустое значение — норма, а не забытый текст.
 * Всё остальное пустое попадёт в дев-консоль и в событие remote_config_empty_values.
 */
export const ALLOWED_EMPTY_CONFIG_PATHS = [
  // продукты, id и цены меняются от конфига к конфигу — дефолтить их нельзя,
  // вместо этого блок не рендерится, если продукт не пришёл
  'products',
  'productsDetails',
  'promocodeOffer.products',
  'specialOffer.products',
  'noFundsOffer.products',
  'upsaleOffer',
  'upsaleFirst',
  'upsaleWithoutTrial',
  'upsaleNutritionBundle.product',
  'upsaleNutritionBundle.upsaleName',
  'upsaleNutritionBundle.recurlyId',
  'upsaleNutritionBundle.priceAmount',
  'upsaleDynamic.product',
  'upsaleDynamic.upsaleName',
  'upsaleDynamic.recurlyId',
  'upsaleDynamic.priceAmount',
  'upsaleDynamic.letterName',
  'upsaleDynamic.fileName',
  'upsaleDynamic.products',
  'changePaymentMethod.products',
  'paywall.product',
  'paymentProject',
  'paymentSystemReserve',
  // поля апсейла из foodzee-вёрстки: voery-страница их не читает,
  // у неё свои timerLabel / skipText / priceNote
  'upsaleDynamic.buttonText',
  'upsaleDynamic.image',
  'upsaleDynamic.descriptionTitle',
  'upsaleDynamic.bottomText',
  // пустое значение задокументировано как отдельное поведение
  'paywall.noPayment', // пусто → блок «no payment» скрыт
  'personalPlanPage.mealPlanTitle', // пусто → динамический тайтл по весу
  'personalPlanPage.moneyBack.title', // пусто → блок money-back скрыт
  'consentCookieModal.placement',
  // пусто → падаем на ключ локализации, дефолт здесь только помешает
  'headerTitle', // <T k={headerTitle || `${tKey}.title`} />
  'errorPopup.buttonTitle', // buttonTitle || t(`${tKey}.${code}.button`)
  // экраны manage-subscription живут своим конфигом — разбираем отдельно,
  // иначе они топят сигнал по онбордингу
  'manageSurvey',
  'mng*',
  // upsale-флоу целиком приходит из мутатора (в дефолтах пустые `{} as X`),
  // внутри — цены и product id, которые дефолтить нельзя
  'addPacksContent',
  'skipTrialContent',
  'earlyAccessContent',
  'daysPlanContent',
  'assistantContent',
  'detailedReportContent',
  'offlineModeContent',
  'unavailableProductPopup',
  'pregnancyEbookContent',
  'meditationEbookContent',
  'landingButtons.priceTitle',
];
